import { readonly } from 'vue';
import { useStorage, watchOnce } from '@vueuse/core';
import { useCallback, useQueryV2 } from '@libs/use';

import * as API from '../requests';
import { SYMBOL_QUERY_KEYS } from '../constants';

export type DeductionsStoreType = {
  buyMaximumValue: number;
  buyValue: number;
  exchangeCode: string | null;
  instrumentTypeCodesField: string[] | null;
  sellMaximumValue: number;
  sellValue: number;
  sectorCodesField: string[] | null;
  subSectorCodesField: string[] | null;
};

const deductionsStore = useStorage<DeductionsStoreType[]>('symbol-deductions', [
  {
    buyMaximumValue: 0,
    buyValue: 0,
    exchangeCode: null,
    instrumentTypeCodesField: null,
    sellMaximumValue: 0,
    sellValue: 0,
    sectorCodesField: null,
    subSectorCodesField: null,
  },
]);

export default function useSymbolDeductions() {
  const onReady = useCallback();

  const { execute: getSymbolDeductions } = useQueryV2(API.getSymbolDeductions, {
    key: SYMBOL_QUERY_KEYS.symbolDeduction,
    staleTime: 1000 * 60 * 5, // 5m
    cacheTime: 1000 * 60 * 60 * 24, // 1d
    onSuccess(result) {
      if (result?.data.items?.length) {
        deductionsStore.value = result.data.items;
      }
    },
  });

  watchOnce([deductionsStore, onReady.callback], ([deductions, callback]) => {
    if (deductions.length && callback) {
      onReady.trigger();
    }
  });

  const findWageConfigBySymbol = (symbol): DeductionsStoreType => {
    const deductionsByType = deductionsStore.value.filter((deduction) => {
      return deduction.instrumentTypeCodesField?.includes(
        symbol.instrumentTypeCode || symbol.instrumentType
      );
    });

    if (!deductionsByType.length) {
      return deductionsStore.value[0];
    }

    if (deductionsByType?.length > 1 && symbol.subSectorCode) {
      const wage = deductionsByType.find((deduction) => {
        return deduction.subSectorCodesField?.includes(symbol.subSectorCode);
      });

      if (wage) {
        return wage;
      }
    }

    return deductionsByType?.[0];
  };

  return {
    getSymbolDeductions,
    findWageConfigBySymbol,
    onDeductionsReady: onReady.on,
    deductions: readonly(deductionsStore),
  };
}
